import { AxiosError, type AxiosRequestConfig } from 'axios';
import { apiService } from '../api/api-client';
import {
  TradeListParams,
  PaginatedTradeList,
  PositionListItem,
  PositionDetail,
  CreateTradeRequest,
  CreateTradeResponse,
  Counterparty,
  Agreement,
  CustodianItem,
  HolidayCheckResponse,
  HolidayItem,
  BenchmarkApiItem,
  PaginatedResponse,
  PositionsSummarySection,
  PositionsSummarySections,
  BoPostingReconcileResult,
  BoPostingReadBack,
  BoPostingRecordResult,
  BoPostingSettleResult,
} from '@/types';
import { buildAgreements, type AgreementApiResponse } from '../counterparty/counterparty-service';
import type { AmendPayload } from './amend-mapper';
import type { ReRatePayload } from './rerate-mapper';

/** The page a counterparty tab list is for (`tabs` on `/counterparties/`). */
export type TabBook = 'trades' | 'collateral';

/** Query params shared by registry list endpoints (counterparties, custodians). */
export interface RegistryQueryParams {
  search?: string;
  limit?: number;
  offset?: number;
  is_active?: boolean;
  /** Repeated in the query string: one request resolves every name. */
  name_exact?: string[];
}

interface CounterpartyApiItem {
  id: number;
  name: string;
  client_id: string;
  country?: string;
  is_active?: boolean;
  agreements?: AgreementApiResponse[];
  parent?: number | null;
  parent_name?: string | null;
  credit_rating?: string;
  notes?: string;
  // DRF DecimalField serialises to a string (e.g. "50000.00"); null = no MTA.
  minimum_transfer_amount?: string | null;
  minimum_transfer_amount_currency?: string;
}

/**
 * Every row matching `params`, a page at a time in the endpoint's own page size. No
 * `limit` is sent: a `?limit=` above the server's `max_limit` comes back CLAMPED with the
 * true count beside it, indistinguishable from the whole set (LR-661), so the loop only
 * counts what arrived and asks for the next offset until `count` is reached.
 */
async function fetchAllRegistryPages<T>(
  path: string,
  params?: Record<string, unknown>,
): Promise<T[]> {
  const collected: T[] = [];
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const page = await apiService.get<PaginatedResponse<T>>(path, {
      params: collected.length > 0 ? { ...params, offset: collected.length } : params,
      // An array param repeats (`name_exact=A&name_exact=B`), as Django reads it, not `name_exact[]=`.
      paramsSerializer: { indexes: null },
    });
    collected.push(...page.results);
    // An empty page ends it whatever `count` says — the total is read per request
    // and drifts under a concurrent write (LR-572 dp #1).
    if (page.results.length === 0 || collected.length >= page.count) break;
  }
  return collected;
}

/** One `/counterparties/` row → the FE `Counterparty` shape. */
const toCounterparty = (cp: CounterpartyApiItem): Counterparty => ({
  id: cp.id,
  name: cp.name,
  clientId: cp.client_id,
  legalEntity: cp.country || 'International',
  isActive: Boolean(cp.is_active),
  // A payload without agreements (e.g. the MSW mock) maps to none, so a downstream
  // single-agreement check resolves to "unknown" and both metric sections show.
  agreements: buildAgreements(cp),
  parentId: cp.parent ?? null,
  parentName: cp.parent_name ?? null,
  creditRating: cp.credit_rating,
  country: cp.country,
  notes: cp.notes,
  // LR-389: parse the DecimalField string to a number for the MTM gate; null = no threshold.
  minimumTransferAmount:
    cp.minimum_transfer_amount != null ? Number(cp.minimum_transfer_amount) : null,
  minimumTransferAmountCurrency: cp.minimum_transfer_amount_currency,
});

/** Rows per export request; a book that fits in one keeps the grid's sort in the CSV. */
export const EXPORT_PAGE_SIZE = 2000;
/** Per-request budget for an export page, which may come over a slow link; the grid keeps the client default. */
export const EXPORT_PAGE_TIMEOUT_MS = 120_000;
/** Waits before the 2nd and 3rd attempt at a failed export page. */
export const EXPORT_PAGE_RETRY_DELAYS_MS = [1000, 3000];

/** A 4xx answers the same on every attempt; a timeout, a dropped connection or a 5xx may not. */
const isWorthRetrying = (error: unknown) =>
  error instanceof AxiosError && (error.response === undefined || error.response.status >= 500);

const fetchExportPage = async (query: TradeListParams): Promise<PaginatedTradeList> => {
  for (let attempt = 0; ; attempt++) {
    try {
      return await tradesService.getTradesList(query, { timeout: EXPORT_PAGE_TIMEOUT_MS });
    } catch (error) {
      if (attempt >= EXPORT_PAGE_RETRY_DELAYS_MS.length || !isWorthRetrying(error)) throw error;
      await new Promise((resolve) => setTimeout(resolve, EXPORT_PAGE_RETRY_DELAYS_MS[attempt]));
    }
  }
};

export const tradesService = {
  /**
   * Get paginated list of positions
   */
  async getTradesList(params?: TradeListParams, config?: AxiosRequestConfig): Promise<PaginatedTradeList> {
    return apiService.get<PaginatedTradeList>('/positions/', { ...config, params });
  },

  /**
   * LR-546: fetch EVERY position matching `params` (no window) for the CSV export.
   * The blotter list is windowed (useTradesInfinite), but an export must carry the
   * whole filtered book. Callers pass the SAME filter/search/CP params the list
   * uses, so the export scope matches exactly what the table shows.
   *
   * A book that fits in one page comes in the grid's sort, as the table shows it. A larger
   * one is paged by id (`ordering=id`, `id_gt` = the last id received) rather than by
   * offset: a trade that re-sorts (a price refresh) or leaves the filter mid-export would
   * shift every later offset and make the next page skip a row. So its CSV lists trades in
   * booking order, bounded by the newest id at the click (`id_lte`): trades booked during
   * the export stay out. Pages are kept small, get their own timeout, and a page that fails
   * on the way (timeout, network, 5xx) is asked again before giving up.
   */
  async getAllTrades(
    params?: Omit<TradeListParams, 'limit' | 'offset'>,
    { onProgress }: { onProgress?: (done: number, total: number) => void } = {},
  ): Promise<PositionListItem[]> {
    // The bound goes out with the first page so that it dates from the click, not from that page.
    const [first, newest] = await Promise.all([
      fetchExportPage({ ...params, limit: EXPORT_PAGE_SIZE }),
      fetchExportPage({ ...params, ordering: '-id', limit: 1 }),
    ]);
    if (first.results.length >= first.count) {
      onProgress?.(first.results.length, first.count);
      return first.results;
    }
    if (newest.results.length === 0) return [];
    const upTo = newest.results[0].id;
    const collected: PositionListItem[] = [];
    let afterId: number | undefined;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const cursor = afterId;
      const page = await fetchExportPage({
        ...params,
        ordering: 'id',
        id_gt: cursor,
        id_lte: upTo,
        limit: EXPORT_PAGE_SIZE,
      });
      // A backend without `id_gt` ignores it and serves the first page forever.
      if (cursor !== undefined && page.results.some((row) => row.id <= cursor)) {
        throw new Error('Trades export: the server did not page by id');
      }
      const before = collected.length;
      collected.push(...page.results);
      // `count` is what is left after the cursor, not the whole book.
      onProgress?.(collected.length, before + page.count);
      // Not `< EXPORT_PAGE_SIZE`: the server clamps `limit` to its max_limit without saying so.
      if (page.results.length === 0 || page.results.length >= page.count) break;
      afterId = page.results[page.results.length - 1].id;
    }
    return collected;
  },

  /**
   * LR-546: whole-book blotter metrics (per-counterparty, per-currency Total
   * Value / Exposure subtotals + counts) for the MetricsBar and tab badges.
   * Honours the same filters + `search` as the list; not paginated. Only the
   * requested section is computed and returned.
   */
  async getPositionsMetrics<S extends PositionsSummarySection>(
    section: S,
    params?: Omit<TradeListParams, 'limit' | 'offset' | 'ordering'>
  ): Promise<PositionsSummarySections[S]> {
    return apiService.get<PositionsSummarySections[S]>('/positions/metrics/', {
      params: { ...params, sections: section },
    });
  },

  /**
   * Get detailed information about a specific position
   */
  async getTradeDetail(id: number): Promise<PositionDetail> {
    return apiService.get<PositionDetail>(`/positions/${id}/`);
  },

  /**
   * Create a new position. With `idempotencyKey`, a resend of the same booking gets
   * back the trade the first send booked instead of booking another.
   */
  async createTrade(data: CreateTradeRequest, idempotencyKey?: string): Promise<CreateTradeResponse> {
    const response = await apiService.post<PositionDetail>(
      '/positions/',
      data,
      idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined,
    );
    return {
      id: response.id,
      status: response.status,
      symbol_id: response.symbol_id,
      counterparty_name: response.counterparty_name,
      quantity: response.quantity,
      trade_date: response.trade_date,
    };
  },

  /**
   * Get list of active positions
   */
  async getActivePositions(): Promise<PositionListItem[]> {
    const response = await apiService.get<PaginatedTradeList>('/positions/', {
      params: { status: 'active' },
    });
    return response.results;
  },

  /**
   * Give notice for an evergreen position
   */
  async giveNotice(id: number, noticeDate?: string): Promise<PositionDetail> {
    return apiService.post<PositionDetail>(`/positions/${id}/give_notice/`, {
      notice_date: noticeDate,
    });
  },

  /**
   * Set actual settlement date for position closure
   */
  async setSettlementDate(id: number, actualSettlementDate: string, reason?: string): Promise<PositionDetail> {
    return apiService.post<PositionDetail>(`/positions/${id}/set_settlement_date/`, {
      actual_settlement_date: actualSettlementDate,
      reason,
    });
  },

  /**
   * Complete settlement atomically for a position in pending_open_settlement
   * (DVP / FOP single-call). For FOP per-leg use settleCash / settleSecurities.
   */
  async settle(id: number): Promise<PositionDetail> {
    return apiService.post<PositionDetail>(`/positions/${id}/settle/`);
  },

  /**
   * Settle the cash leg of an FOP position independently (LR-291 Stage 6).
   * Requires status=pending_open_settlement, delivery_type=fop.
   */
  async settleCash(id: number): Promise<PositionDetail> {
    return apiService.post<PositionDetail>(`/positions/${id}/settle_cash/`);
  },

  /**
   * Settle the securities leg of an FOP position independently.
   */
  async settleSecurities(id: number): Promise<PositionDetail> {
    return apiService.post<PositionDetail>(`/positions/${id}/settle_securities/`);
  },

  /**
   * Reset a `rejected` position back to `pending` so the next hourly
   * `process_pending_trades` run retries the BO call (LR-408). Used after
   * the operator has fixed the underlying BO reference data (missing
   * counterparty account, unmapped symbol, etc.).
   */
  async retry(id: number): Promise<PositionDetail> {
    return apiService.post<PositionDetail>(`/positions/${id}/retry/`);
  },

  /**
   * LR-711: resolve this trade's stuck BackOffice postings, on demand.
   *
   * The same decision the reconciliation cron makes, for the operator looking at
   * the trade now. It asks BackOffice nothing and books nothing: a posting stamped
   * with a request id is unblocked so the ordinary cron re-posts it, and BackOffice
   * deduplicates a repeat — so the worst case is a 409 the posting path already
   * knows how to read.
   *
   * `outcomes` says what happened per posting; `legacy` means one predates the
   * request id and only a person who has checked BackOffice can settle it.
   */
  async reconcileBoPosting(id: number): Promise<BoPostingReconcileResult> {
    return apiService.post<BoPostingReconcileResult>(
      `/positions/${id}/bo-posting/reconcile/`,
    );
  },

  /**
   * LR-706 (g): what BackOffice holds for this trade's LEGACY posting.
   *
   * Read-only, and the reason the write below has anything to confirm. A legacy
   * posting carries no request id, so BackOffice cannot recognise a repeat and we
   * never re-send it — reading is the only safe move. Answers 400 with a sentence
   * when there is nothing to show, and each sentence is a different instruction:
   * the read failed, BackOffice holds nothing, only half the posting is there.
   */
  async readBackBoPosting(id: number): Promise<BoPostingReadBack> {
    return apiService.get<BoPostingReadBack>(
      `/positions/${id}/bo-posting/read-back/`,
    );
  },

  /**
   * LR-706 (g): write our side of a posting BackOffice turns out to hold.
   *
   * Admin-gated, and the only action in this family that writes the ledger. Every
   * figure recorded is BackOffice's own and `external_id` is its uuid; no POST is
   * made. `uuids` are the transactions the operator saw — the server reads
   * BackOffice again and refuses if the set moved in between.
   */
  async recordBoPostingFromBackoffice(
    id: number, uuids: string[],
  ): Promise<BoPostingRecordResult> {
    return apiService.post<BoPostingRecordResult>(
      `/positions/${id}/bo-posting/record-from-backoffice/`, { uuids },
    );
  },

  /**
   * LR-715: close a posting BackOffice contradicted itself about, by hand.
   *
   * The one exit BackOffice cannot open. Reconciling re-asks it, and for a
   * `confirmed_unrecorded` posting only a 409 settles the row — when the answer is
   * a 200 instead, BackOffice booked a request id it had already accepted, so it
   * may now hold the posting twice and the row is deliberately left open. Nothing
   * it says later can close it; only a person who has reconciled the books there
   * can, and `reason` is the whole record of what they did.
   *
   * Admin-gated (403 otherwise). Touches only the posting log — not the guard, not
   * the trade.
   */
  async settleBoPostingManually(
    id: number, reason: string,
  ): Promise<BoPostingSettleResult> {
    return apiService.post<BoPostingSettleResult>(
      `/positions/${id}/bo-posting/settle-manually/`, { reason },
    );
  },

  /**
   * Manually fire the Trade Date action on a `pending` position ("Open now",
   * LR-544) instead of waiting for the hourly `process_pending_trades` cron.
   * pending → pending_open_settlement (BO hold) or T+0 → active. A BO failure
   * is non-fatal (position stays pending) and returns a 502 with the error
   * text; bo_retry_count is not incremented (as in the create path).
   *
   * LR-582 adds two refusals where nothing was sent to BO at all: 409 when the
   * posting guard is holding this trade (an earlier posting is in flight or
   * ended unconfirmed — `detail` says which and names the guard key), and 503
   * when the guard store itself is unreachable. Neither is a statement about
   * BackOffice, and neither consumes a retry.
   */
  async processTradeDate(id: number): Promise<PositionDetail> {
    return apiService.post<PositionDetail>(`/positions/${id}/process_trade_date/`);
  },

  /**
   * Terminate position and schedule closure
   */
  async terminate(id: number, actualSettlementDate?: string, reason?: string): Promise<PositionDetail> {
    return apiService.post<PositionDetail>(`/positions/${id}/terminate/`, {
      actual_settlement_date: actualSettlementDate,
      reason,
    });
  },

  /**
   * Book a PENDING partial recall (lend side) / return (borrow side) against a
   * position, reducing its MTM-markable remaining quantity (LR-388). Kind is
   * derived server-side from the trade side.
   */
  async partialRecall(
    id: number,
    payload: { quantity: number; settlement_date: string },
  ): Promise<PositionDetail> {
    return apiService.post<PositionDetail>(`/positions/${id}/partial_recall/`, payload);
  },

  /**
   * LR-470: settle a pending partial recall/return — reduces the booked quantity,
   * posts the partial reverse transfer, and reprices earnings/CV. Closes the
   * position on full recall.
   */
  async settlePartialRecall(
    positionId: number,
    recallId: number,
    // LR-515: optional settlement-date override applied atomically at settle. Omitted
    // → the backend settles on the already-stored contractual date.
    settlementDate?: string,
  ): Promise<PositionDetail> {
    return apiService.post<PositionDetail>(
      `/positions/${positionId}/partial_recalls/${recallId}/settle/`,
      settlementDate ? { settlement_date: settlementDate } : {},
    );
  },

  /**
   * LR-470: cancel a pending partial recall/return before it settles — restores
   * the MTM-markable remaining quantity. No Backoffice posting.
   */
  async cancelPartialRecall(positionId: number, recallId: number): Promise<PositionDetail> {
    return apiService.post<PositionDetail>(
      `/positions/${positionId}/partial_recalls/${recallId}/cancel/`,
      {},
    );
  },

  /**
   * LR-470: override the contractual settlement date of a pending partial
   * recall/return so billing stays correct when settlement is delayed. Only a
   * pending row can be re-dated.
   */
  async setPartialRecallSettlementDate(
    positionId: number,
    recallId: number,
    settlementDate: string,
  ): Promise<PositionDetail> {
    return apiService.post<PositionDetail>(
      `/positions/${positionId}/partial_recalls/${recallId}/settlement_date/`,
      { settlement_date: settlementDate },
    );
  },

  /**
   * Amend a PENDING position's booked "numbers" via PATCH /positions/{id}/
   * (LR-478 quantity, widened in LR-484 to price/margin/rate/dates). The backend
   * (PositionUpdateSerializer) gates the status (pending only) and recomputes
   * margin_price/contract_value/total_rate on save. Send only the changed fields
   * (see diffAmendPayload).
   */
  async updatePosition(id: number, payload: AmendPayload): Promise<PositionDetail> {
    return apiService.patch<PositionDetail>(`/positions/${id}/`, payload);
  },

  /**
   * LR-528: re-rate an ACTIVE (or backdated MATURED) position — "from this date
   * the rate is now X". Changes only the rate leg of the booked mode (rebate →
   * benchmark_rate/spread_bps, fee → fee_percent) via POST /positions/{id}/re-rate/.
   * effective_date today = T0 (applied immediately), future = booked pending,
   * past = backdated (accruals recomputed, LR-527). Returns the re-priced
   * PositionDetail; the backend gates status (409) and the fields/date (400).
   */
  async reRate(id: number, payload: ReRatePayload): Promise<PositionDetail> {
    return apiService.post<PositionDetail>(`/positions/${id}/re-rate/`, payload);
  },

  /**
   * Give a trade booked without a cash pool its pool. The backend runs the booking checks again
   * (400) and gates the status (409); a fee trade's collateral enters the pool on the spot.
   */
  async assignCashPool(id: number, cashPool: number): Promise<PositionDetail> {
    return apiService.post<PositionDetail>(`/positions/${id}/assign-cash-pool/`, {
      cash_pool: cashPool,
    });
  },

  /**
   * One page of the counterparties that get a tab on `book`'s page: the active ones and the
   * deactivated ones with a book there, by name.
   */
  async getCounterpartyTabs(
    book: TabBook,
    limit: number,
    offset: number,
  ): Promise<{ count: number; results: Counterparty[] }> {
    const page = await apiService.get<PaginatedResponse<CounterpartyApiItem>>('/counterparties/', {
      params: { tabs: book, limit, offset },
    });
    return { count: page.count, results: page.results.map(toCounterparty) };
  },

  /** Every counterparty matching `params` (LR-661). */
  async getAllCounterparties(
    params?: Omit<RegistryQueryParams, 'limit' | 'offset'>
  ): Promise<Counterparty[]> {
    const rows = await fetchAllRegistryPages<CounterpartyApiItem>('/counterparties/', params);
    return rows.map(toCounterparty);
  },

  /**
   * Get agreements for a counterparty
   * TODO: Replace with actual backend endpoint when available
   */
  async getAgreements(_counterpartyId: string): Promise<Agreement[]> {
    // Mock implementation - replace with actual API call
    return Promise.resolve([
      {
        id: 'agr1',
        type: 'gmsla',
        status: 'ACTIVE',
        valid_from: '2024-01-01',
        allowed_trade_types: ['lend', 'borrow'],
        default_settlement_venue: 'dtc',
        default_delivery_type: 'dvp',
      },
    ]);
  },

  /**
   * Get list of custodians.
   * The endpoint is paginated ({count, results}); we unwrap to the rows.
   * Accepts registry query params (search / limit / is_active).
   */
  async getCustodians(params?: RegistryQueryParams): Promise<CustodianItem[]> {
    const response = await apiService.get<PaginatedResponse<CustodianItem>>(
      '/references/custodians/',
      { params }
    );
    return response.results;
  },

  /**
   * Every custodian matching `params`. Same pagination as the counterparties, so its
   * two-step truncated at the same cap — small registry today, same defect (LR-661).
   */
  async getAllCustodians(
    params?: Omit<RegistryQueryParams, 'limit' | 'offset'>
  ): Promise<CustodianItem[]> {
    return fetchAllRegistryPages<CustodianItem>('/references/custodians/', params);
  },

  /**
   * Check if a date is a public holiday
   */
  async checkHoliday(date: string): Promise<HolidayCheckResponse> {
    return apiService.get<HolidayCheckResponse>('/references/holidays/check/', {
      params: { date },
    });
  },

  /**
   * Fetch the full list of holidays. Used by UI modals to filter client-side
   * and avoid per-keystroke API hits on date inputs.
   */
  async getHolidays(): Promise<HolidayItem[]> {
    return apiService.get<HolidayItem[]>('/references/holidays/');
  },

  /**
   * Fetch benchmark reference rates (SOFR, OBFR, ESTR, SONIA, TONAR). The
   * Trade Entry form uses these to populate the benchmark rate and the
   * "Current value" hint instead of hardcoding them (LR-435).
   */
  async getBenchmarks(): Promise<BenchmarkApiItem[]> {
    return apiService.get<BenchmarkApiItem[]>('/references/benchmarks/');
  },
};
